import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useStyles } from './MessagePreview.styles';
import { useEffect, useRef, useState } from 'react';
import { deleteMessage, getMessageContent } from '../../../services/communication/communication.service';
import { AxiosResponse } from 'axios';
import { mailboxActions } from '../../../reducers/mailboxReducer/mailboxSlice';
import { useLayoutContext } from '../../../Context/Layout.context';
import OutlinedButton from '../../buttons/OutlinedButton.component';
import { ReactComponent as ReplyIcon } from '../../../assets/icons/reply.svg';
import { ReactComponent as ForwardIcon } from '../../../assets/icons/forward.svg';
import { ReactComponent as TrashIcon } from '../../../assets/icons/trash.svg';
import {
  FlagId,
  FolderMessageContent,
  foldersWithDeleteEnabled,
  foldersWithForwardEnabled,
} from '../../../models/communication.types';
import MessageFlag from '../MessageFlag.component';
import { MenuItemWithImage } from '../../DropdownMenu/DropdownMenu.component';
import ClearIcon from '@mui/icons-material/Clear';
import { Prompt } from '../../Prompt/Prompt.component';
import { handleRemoveMessageFlag, handleSetMessageFlag } from '../MessagesList/utils';
import MessageContentDisplay from './MessageContentDisplay.component';
import { fill } from '../../../utils/contrastSvgAttributes';

const MessagePreview: React.FC = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { openAlert } = useLayoutContext();
  const prevMessageId = useRef<string | null>(null);
  const activeFolder = useSelector((state: RootState) => state.mailbox.activeFolder);
  const activeMessageId = useSelector((state: RootState) => state.mailbox.activeMessageId);
  const replyMessageId = useSelector((state: RootState) => state.mailbox.replyMessageId);
  const forwardMessageId = useSelector((state: RootState) => state.mailbox.forwardMessageId);
  const actionsHidden = replyMessageId || forwardMessageId;
  const loadedMessageContent = useSelector((state: RootState) => state.mailbox.loadedMessageContent);

  const actionMenuRef = useRef<SVGSVGElement | null>(null);

  const [actionMenuAnchor, setActionMenuAnchor] = useState<SVGSVGElement | undefined>(undefined);
  const [messageDeletingConfirmPrompt, setMessageDeletingConfirmPrompt] = useState<boolean>(false);

  const forwardingEnabled = foldersWithForwardEnabled.includes(activeFolder);

  useEffect(() => {
    if (activeMessageId && (activeMessageId !== prevMessageId.current || !loadedMessageContent)) {
      dispatch(mailboxActions.setPrevMessageId(prevMessageId.current));
      prevMessageId.current = activeMessageId;
      getMessageContent(activeMessageId).then(
        (response: AxiosResponse<FolderMessageContent>) => {
          dispatch(mailboxActions.loadMessage(response.data));
        },
        () => {
          openAlert('get_error');
        },
      );
    }
  }, [activeMessageId, loadedMessageContent]);

  if (!loadedMessageContent) {
    return <></>;
  }

  const handleReply = () => {
    dispatch(mailboxActions.openReplyForMessage({ messageId: activeMessageId, messageContent: loadedMessageContent }));
  };

  const handleForward = () => {
    dispatch(mailboxActions.openForwardForMessage(activeMessageId));
  };

  const handleUpdateCurrentMessageFlag = (flag: FlagId | null): void => {
    dispatch(mailboxActions.updateCurrentMessageFlag(flag));
    if (activeMessageId) {
      dispatch(mailboxActions.setMessageToUpdateFlag({ messageId: activeMessageId, flag: flag }));
    }
  };

  const actionItems: MenuItemWithImage[] = [
    {
      text: 'Odpowiedz',
      icon: <ReplyIcon fill={fill} />,
      onClick: handleReply,
    },
    ...(forwardingEnabled
      ? [
          {
            text: 'Przekaż dalej',
            icon: <ForwardIcon fill={fill} />,
            onClick: handleForward,
          },
        ]
      : []),
    ...(foldersWithDeleteEnabled.includes(activeFolder)
      ? [
          {
            text: 'Usuń wiadomość',
            icon: <TrashIcon fill={fill} />,
            onClick: () => setMessageDeletingConfirmPrompt(true),
          },
        ]
      : []),
    {
      text: 'Oznacz jako ważną',
      icon: <MessageFlag flagId={FlagId.Green} />,
      onClick: () => {
        handleSetMessageFlag(activeMessageId, FlagId.Green, handleUpdateCurrentMessageFlag, openAlert);
        setActionMenuAnchor(undefined);
      },
    },
    {
      text: 'Oznacz jako bardzo ważną',
      icon: <MessageFlag flagId={FlagId.Yellow} />,
      onClick: () => {
        handleSetMessageFlag(activeMessageId, FlagId.Yellow, handleUpdateCurrentMessageFlag, openAlert);
        setActionMenuAnchor(undefined);
      },
    },
    {
      text: 'Oznacz jako pilną',
      icon: <MessageFlag flagId={FlagId.Red} />,
      onClick: () => {
        handleSetMessageFlag(activeMessageId, FlagId.Red, handleUpdateCurrentMessageFlag, openAlert),
          setActionMenuAnchor(undefined);
      },
    },
    {
      disabled: loadedMessageContent.flag === null,
      text: 'Usuń oznaczenie',
      icon: <ClearIcon />,
      onClick: () => {
        handleRemoveMessageFlag(activeMessageId, handleUpdateCurrentMessageFlag, openAlert),
          setActionMenuAnchor(undefined);
      },
    },
  ];

  const handleDeleteMessage = (messageId: string) => {
    deleteMessage(messageId)
      .then(
        () => {
          openAlert('success', 'Wiadomość została pomyślnie usunięta');
          dispatch(mailboxActions.addMessageToDelete(messageId));
        },
        () => openAlert('error', 'Usunięcie wiadomości nie powiodło się. Spróbuj ponownie później'),
      )
      .finally(() => {
        setMessageDeletingConfirmPrompt(false);
      });
  };

  return (
    <div className={classes.messagePreview}>
      <MessageContentDisplay
          loadedMessageContent={loadedMessageContent}
          actionsHidden={actionsHidden}
          activeFolder={activeFolder}
          actionMenuRef={actionMenuRef}
          actionMenuAnchor={actionMenuAnchor}
          setActionMenuAnchor={setActionMenuAnchor}
          actionItems={actionItems}
        />
        
        {!actionsHidden && (
          <div className={classes.buttonsFooter}>
            <OutlinedButton startIcon={<ReplyIcon fill={fill} title="ikona odpowiedz" />} onClick={handleReply}>
              Odpowiedz
            </OutlinedButton>
            {forwardingEnabled && (
              <OutlinedButton
                endIcon={<ForwardIcon fill={fill} title="ikona odpowiedz" />}
                style={{ marginLeft: '10px' }}
                onClick={handleForward}
              >
                Przekaż dalej
              </OutlinedButton>
            )}
          </div>
        )}
      {messageDeletingConfirmPrompt && (
        <Prompt
          type="confirmation"
          mainText="Czy na pewno chcesz usunąć wiadomość?"
          mainActionButtonText="TAK"
          onConfirm={() => handleDeleteMessage(loadedMessageContent.messageId)}
          onCancel={() => setMessageDeletingConfirmPrompt(false)}
        />
      )}
    </div>
  );
};

export default MessagePreview;
